import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useState
} from 'react';
import { useSignupAction } from '../actions';
import {
    UIButton,
    UICheckbox,
    UIExternalLinkTextButton,
    UIFormInput,
    UIInlineAlert,
    UISpacer,
    UIToast
} from '@/ui';
import styles from './signup.module.scss';
import { Trans } from 'react-i18next';
import { useMeTranslation } from '../hooks';
import {
    FIRST_NAME_MAX_LENGTH,
    LAST_NAME_MAX_LENGTH,
    PASSWORD_MAX_LENGTH,
    validateFirstName,
    validateLastName,
    validatePassword
} from '@/common/util';

type SignupProps = {
    token: string;
    onSuccess?: () => void;
};
export function Signup(props: SignupProps): ReactElement {
    const { token, onSuccess } = props;

    const { signup, loading, error, clearError } = useSignupAction();

    return (
        <>
            {error && (
                <>
                    <UIInlineAlert text={error} onClose={clearError} />
                    <UISpacer multiplier={2} />
                </>
            )}
            <SignupForm
                loading={loading}
                onSubmit={(data) => {
                    signup({
                        token,
                        firstName: data.firstName,
                        lastName: data.lastName,
                        password: data.password,
                        legal: data.legal,
                        marketing: data.marketing
                    })
                        .then(() => {
                            onSuccess?.();
                        })
                        .catch(() => {}); // Handled by hook
                }}
            />
        </>
    );
}

type SignupFormProps = {
    onSubmit?: (data: {
        firstName: string;
        lastName: string;
        password: string;
        legal: boolean;
        marketing: boolean;
    }) => void;
    loading?: boolean;
};
export function SignupForm(props: SignupFormProps): ReactElement {
    const { t } = useMeTranslation();

    const { onSubmit, loading } = props;

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * First name
     */
    const [firstName, setFirstName] = useState('');
    const [firstNameError, setFirstNameError] = useState('');
    const checkFirstName = useCallback(
        (firstName: string): string => {
            const error = validateFirstName(firstName);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setFirstNameError(checkFirstName(firstName));
        }
    }, [firstName, showRealTimeErrors, checkFirstName]);

    /**
     * Last name
     */
    const [lastName, setLastName] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const checkLastName = useCallback(
        (lastName: string): string => {
            const error = validateLastName(lastName);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setLastNameError(checkLastName(lastName));
        }
    }, [lastName, showRealTimeErrors, checkLastName]);

    /**
     * Password
     */
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const checkPassword = useCallback(
        async (password: string): Promise<string> => {
            const error = await validatePassword(password);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        let isMounted = true;
        if (showRealTimeErrors) {
            checkPassword(password)
                .then((response) => {
                    if (isMounted) {
                        setPasswordError(response);
                    }
                })
                .catch(() => {
                    if (isMounted) {
                        setPasswordError(t('shared.errors.generic'));
                    }
                });
        }

        return () => {
            isMounted = false; // Cleanup function
        };
    }, [password, showRealTimeErrors, checkPassword, t]);

    /**
     * Confirm password
     */
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const checkConfirmPassword = useCallback(
        (confirmPassword: string): string => {
            if (confirmPassword !== password) {
                return t('shared.fields.password.error_no_match');
            }
            return '';
        },
        [t, password]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setConfirmPasswordError(checkConfirmPassword(confirmPassword));
        }
    }, [confirmPassword, showRealTimeErrors, checkConfirmPassword]);

    /**
     * Legal
     */
    const [legal, setLegal] = useState(false);
    const [legalError, setLegalError] = useState('');
    const checkLegal = useCallback(
        (legal: boolean): string => {
            if (!legal) {
                return t('shared.legal.error_required');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setLegalError(checkLegal(legal));
        }
    }, [legal, showRealTimeErrors, checkLegal]);

    /**
     * Marketing
     */
    const [marketing, setMarketing] = useState(false);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);
        checkPassword(password)
            .then((isPasswordValid) => {
                if (
                    !checkFirstName(firstName) &&
                    !checkLastName(lastName) &&
                    !isPasswordValid &&
                    !checkConfirmPassword(confirmPassword) &&
                    !checkLegal(legal)
                ) {
                    setShowRealTimeErrors(false);
                    onSubmit?.({
                        firstName,
                        lastName,
                        password,
                        legal,
                        marketing
                    });
                }
            })
            .catch(() => {
                UIToast.error(t('shared.errors.generic'));
            });
    };

    return (
        <div className={styles.loginForm}>
            <form onSubmit={handleSubmit} noValidate>
                <UIFormInput
                    name="firstName"
                    label={t('shared.fields.first_name.label')}
                    value={firstName}
                    autoFocus={true}
                    type="text"
                    errorText={firstNameError}
                    required
                    maxLength={FIRST_NAME_MAX_LENGTH}
                    onChange={setFirstName}
                />

                <UISpacer multiplier={2} />

                <UIFormInput
                    name="lastName"
                    label={t('shared.fields.last_name.label')}
                    value={lastName}
                    type="text"
                    errorText={lastNameError}
                    required
                    maxLength={LAST_NAME_MAX_LENGTH}
                    onChange={setLastName}
                />

                <UISpacer multiplier={2} />

                <UIFormInput
                    name="password"
                    label={t('shared.fields.password.label')}
                    value={password}
                    type="password"
                    errorText={passwordError}
                    required
                    maxLength={PASSWORD_MAX_LENGTH}
                    onChange={setPassword}
                />

                <UISpacer multiplier={2} />

                <UIFormInput
                    name="confirmPassword"
                    label={t('shared.fields.password.confirm_label')}
                    value={confirmPassword}
                    type="password"
                    errorText={confirmPasswordError}
                    required
                    maxLength={PASSWORD_MAX_LENGTH}
                    onChange={setConfirmPassword}
                />

                <UISpacer multiplier={2} />

                <UICheckbox
                    name="legal"
                    checked={legal}
                    onChange={setLegal}
                    description={
                        <Trans
                            ns="me"
                            i18nKey="shared.legal.description"
                            components={{
                                a1: (
                                    <UIExternalLinkTextButton
                                        variant="primary"
                                        href={
                                            'https://www.safepointapp.com/end-user-license-agreement'
                                        }
                                    />
                                ),
                                a2: (
                                    <UIExternalLinkTextButton
                                        variant="primary"
                                        href={
                                            'https://www.safepointapp.com/privacy-policy'
                                        }
                                    />
                                )
                            }}
                        />
                    }
                    errorText={legalError}
                    required
                />

                <UISpacer multiplier={2} />

                <UICheckbox
                    name="marketing"
                    checked={marketing}
                    onChange={setMarketing}
                    description={t('shared.legal.marketing_opt_in')}
                />

                <UISpacer multiplier={2} />

                <UIButton
                    variant="primary"
                    type="submit"
                    loading={loading}
                    disabled={loading}
                >
                    {t('components.signup.submit')}
                </UIButton>
            </form>
        </div>
    );
}
